import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router";

import GuestOnly from "@/layouts/guest-only";
import JoinLayout from "@/layouts/join-layout";

// Each page is its own chunk, loaded on first visit.
function page(load: () => Promise<{ default: ComponentType }>) {
  const Component = lazy(load);
  return (
    <Suspense fallback={null}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  { path: "/", element: <Navigate to="/dashboard" replace /> },

  // Auth pages — signed-in visitors are redirected away.
  {
    element: <GuestOnly />,
    children: [
      { path: "/login", element: page(() => import("@/pages/auth/login/page")) },
      { path: "/signup", element: page(() => import("@/pages/auth/signup/page")) },
      { path: "/forgot-password", element: page(() => import("@/pages/auth/forgot-password/page")) },
    ],
  },
  { path: "/reset-password", element: page(() => import("@/pages/auth/reset-password/page")) },

  // Invitation links work signed in or out.
  {
    element: <JoinLayout />,
    children: [{ path: "/join/:token", element: page(() => import("@/pages/join/token/page")) }],
  },

  // Signed-in app — DashboardShell redirects anonymous visitors to /login.
  {
    element: page(() => import("@/pages/dashboard/dashboard-shell")),
    children: [
      { path: "/dashboard", element: page(() => import("@/pages/dashboard/dashboard/page")) },
      { path: "/inbox", element: page(() => import("@/pages/dashboard/inbox/page")) },
      { path: "/contacts", element: page(() => import("@/pages/dashboard/contacts/page")) },
      { path: "/pipelines", element: page(() => import("@/pages/dashboard/pipelines/page")) },
      { path: "/broadcasts", element: page(() => import("@/pages/dashboard/broadcasts/page")) },
      { path: "/broadcasts/new", element: page(() => import("@/pages/dashboard/broadcasts/new/page")) },
      { path: "/broadcasts/:id", element: page(() => import("@/pages/dashboard/broadcasts/detail/page")) },
      { path: "/automations", element: page(() => import("@/pages/dashboard/automations/page")) },
      { path: "/automations/new", element: page(() => import("@/pages/dashboard/automations/new/page")) },
      { path: "/automations/:id/edit", element: page(() => import("@/pages/dashboard/automations/detail/edit/page")) },
      { path: "/automations/:id/logs", element: page(() => import("@/pages/dashboard/automations/detail/logs/page")) },
      { path: "/flows", element: page(() => import("@/pages/dashboard/flows/page")) },
      { path: "/flows/:id", element: page(() => import("@/pages/dashboard/flows/detail/page")) },
      { path: "/flows/:id/runs", element: page(() => import("@/pages/dashboard/flows/detail/runs/page")) },
      { path: "/agents", element: page(() => import("@/pages/dashboard/agents/page")) },
      { path: "/notifications", element: page(() => import("@/pages/dashboard/notifications/page")) },
      { path: "/settings", element: page(() => import("@/pages/dashboard/settings/page")) },
    ],
  },

  { path: "*", element: <Navigate to="/dashboard" replace /> },
]);
